import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useProfileStore } from "../store/useProfileStore";
import actions from "../config.json";

const clamp01 = (value) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }

  if (value <= 0) {
    return 0;
  }

  if (value >= 1) {
    return 1;
  }

  return value;
};

const VALUE_PROPENSITY_LOOKUP = {
  enthusiast: 0.9,
  interested: 0.7,
  casual: 0.6,
  default: 0.6,
  yes: 0.75,
  true: 0.75,
  active: 0.75,
  engaged: 0.85,
  neutral: 0.5,
  maybe: 0.5,
  no: 0,
  false: 0,
  "not interested": 0,
  notinterested: 0,
  nointr: 0,
};

const PASSION_KEY_MAPPING = {
  beach: "beach",
  golf: "golf",
  ski: "ski",
  beachWaterActivities: "beach",
  mountainWinterSports: "ski",
  culinary: "culinary",
  culture: "culture",
  music: "music",
  spa: "spa",
  surf: "surf",
  dive: "dive",
  hike: "hike",
  paddle: "paddle",
  fish: "fish",
  bike: "bike",
  safari: "safari",
};

const LEGACY_CATEGORY_MAPPING = {
  GOLF_LOV: "golf",
  SPA_LOV: "spa",
  INTERESTS_AND_HOBBIES_LOV: {
    BCH: "beach",
    WNTRACT: "ski",
    COOK: "culinary",
    CULTR: "culture",
    MUSIC: "music",
    SURF: "surf",
    FISH: "fish",
    BIKE: "bike",
    HIKE: "hike",
  },
};

export const PASSION_DEFINITIONS = {
  beach: { label: "Beach", icon: "beach" },
  golf: { label: "Golf", icon: "golf" },
  ski: { label: "Ski", icon: "ski" },
  spa: { label: "Spa", icon: "spa" },
  culture: { label: "Culture", icon: "culture" },
  music: { label: "Music", icon: "music" },
  culinary: { label: "Culinary", icon: "culinary" },
  surf: { label: "Surf", icon: "surf" },
  dive: { label: "Dive", icon: "dive" },
  hike: { label: "Hike", icon: "hike" },
  paddle: { label: "Paddle", icon: "paddle" },
  fish: { label: "Fishing", icon: "fish" },
  bike: { label: "Cycling", icon: "bike" },
  safari: { label: "Safari", icon: "safari" },
};

export const POPULAR_PASSION_IDS = ["beach", "golf", "ski"];

const formatLabel = (id) => {
  const definition = PASSION_DEFINITIONS[id];
  if (definition?.label) {
    return definition.label;
  }

  if (!id) {
    return "";
  }

  return id
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const interpretStringPropensity = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const numeric = Number(trimmed);
  if (!Number.isNaN(numeric)) {
    return clamp01(numeric);
  }

  const normalized = trimmed.toLowerCase();
  if (Object.prototype.hasOwnProperty.call(VALUE_PROPENSITY_LOOKUP, normalized)) {
    return VALUE_PROPENSITY_LOOKUP[normalized];
  }

  // Treat any other non-empty string as a positive declaration
  return VALUE_PROPENSITY_LOOKUP.default;
};

const resolvePropensity = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return clamp01(value) ?? (value > 0 ? 1 : 0);
  }

  if (typeof value === "boolean") {
    return value ? 0.75 : 0;
  }

  if (typeof value === "string") {
    return interpretStringPropensity(value);
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? 0.75 : 0;
  }

  if (typeof value === "object") {
    if (typeof value.propensity === "number") {
      return clamp01(value.propensity) ?? (value.propensity > 0 ? 1 : 0);
    }

    if (typeof value.score === "number") {
      return clamp01(value.score) ?? (value.score > 0 ? 1 : 0);
    }

    if (typeof value.confidence === "number") {
      return clamp01(value.confidence) ?? (value.confidence > 0 ? 1 : 0);
    }

    if (value.value !== undefined) {
      return resolvePropensity(value.value);
    }

    if (value.selected !== undefined) {
      return resolvePropensity(value.selected);
    }
  }

  return null;
};

const isTruthyPreference = (input) => {
  if (input === null || input === undefined) {
    return false;
  }

  if (typeof input === "boolean") {
    return input;
  }

  if (typeof input === "number") {
    return input > 0;
  }

  if (typeof input === "string") {
    const normalized = input.trim().toLowerCase();
    if (!normalized || normalized === "false" || normalized === "no" || normalized === "notinterested" || normalized === "nointr") {
      return false;
    }
    return true;
  }

  if (Array.isArray(input)) {
    return input.length > 0;
  }

  if (typeof input === "object") {
    if (input.value !== undefined) {
      return isTruthyPreference(input.value);
    }

    if (input.selected !== undefined) {
      return isTruthyPreference(input.selected);
    }

    if (input.propensity !== undefined) {
      return isTruthyPreference(input.propensity);
    }

    return Object.values(input).some((child) => isTruthyPreference(child));
  }

  return false;
};

const buildPassionMeta = (id, sourceData, { fallbackPropensity = 0.75, isFallback = false } = {}) => {
  if (!id) {
    return null;
  }

  const definition = PASSION_DEFINITIONS[id] || {};
  const rawPropensity = resolvePropensity(sourceData);
  const propensity = rawPropensity !== null ? rawPropensity : fallbackPropensity;

  if (propensity === null) {
    return null;
  }

  return {
    id,
    label: definition.label || formatLabel(id),
    icon: definition.icon || null,
    propensity,
    isFallback,
  };
};

const extractFromObject = (passionsObject) => {
  const results = [];

  Object.keys(passionsObject).forEach((key) => {
    const mappedId = PASSION_KEY_MAPPING[key];
    if (!mappedId) {
      return;
    }

    const passionData = passionsObject[key];
    if (!isTruthyPreference(passionData)) {
      return;
    }

    const meta = buildPassionMeta(mappedId, passionData, {
      fallbackPropensity: 0.75,
    });

    if (meta && !results.some((entry) => entry.id === meta.id)) {
      results.push(meta);
    }
  });

  return results;
};

const extractFromLegacy = (passionsArray) => {
  const results = [];

  passionsArray.forEach((category) => {
    if (!category || !category.category || !Array.isArray(category.values)) {
      return;
    }

    if (category.category === "GOLF_LOV" && category.values.length > 0) {
      const meta = buildPassionMeta("golf", category.values, {
        fallbackPropensity: 0.75,
      });
      if (meta) {
        results.push(meta);
      }
    } else if (category.category === "SPA_LOV" && category.values.length > 0) {
      const meta = buildPassionMeta("spa", category.values, {
        fallbackPropensity: 0.75,
      });
      if (meta) {
        results.push(meta);
      }
    } else if (category.category === "INTERESTS_AND_HOBBIES_LOV" && category.values.length > 0) {
      category.values.forEach((value) => {
        const mapped = LEGACY_CATEGORY_MAPPING.INTERESTS_AND_HOBBIES_LOV[value];
        if (!mapped) {
          return;
        }

        const meta = buildPassionMeta(mapped, category.values, {
          fallbackPropensity: 0.7,
        });

        if (meta && !results.some((entry) => entry.id === meta.id)) {
          results.push(meta);
        }
      });
    }
  });

  return results;
};

const extractSelectedPassionsMeta = (passions) => {
  if (!passions) {
    return [];
  }

  try {
    if (typeof passions === "object" && !Array.isArray(passions)) {
      return extractFromObject(passions);
    }

    if (Array.isArray(passions)) {
      return extractFromLegacy(passions);
    }
  } catch (error) {
    console.error("Error parsing passions data:", error);
  }

  return [];
};

/**
 * Custom hook to fetch and manage user's passions data from the server
 * This hook calls the get-passions action to retrieve the user's passion preferences
 * stored in Adobe Experience Platform
 */
export const usePassionsData = () => {
  const [searchParams] = useSearchParams();
  const acid = searchParams.get("acid");
  const {
    profile: profileInfo,
    loading,
    error,
    fetchProfile,
  } = useProfileStore();
  const [passions, setPassions] = useState(null);

  const applyProfilePassions = useCallback((profile) => {
    if (!profile) {
      setPassions(null);
      return;
    }

    const { passions: profilePassions } = profile;

    if (Array.isArray(profilePassions)) {
      const mapped = profilePassions.reduce((acc, passion) => {
        const key = String(passion || "").trim();
        if (key) {
          acc[key] = { value: "default", propensity: 0.75 };
        }
        return acc;
      }, {});

      setPassions(mapped);
      return;
    }

    if (
      profilePassions &&
      typeof profilePassions === "object" &&
      Object.keys(profilePassions).length > 0
    ) {
      setPassions(profilePassions);
      return;
    }

    setPassions(null);
  }, []);

  useEffect(() => {
    if (acid) {
      fetchProfile(acid);
    }
  }, [acid, fetchProfile]);

  useEffect(() => {
    if (profileInfo) {
      applyProfilePassions(profileInfo);
    } else if (error) {
      // On error, keep passions as null - don't fallback to defaults
      // This allows the UI to properly show the CollectionsModule for passion selection
      setPassions(null);
    }
  }, [profileInfo, error, applyProfilePassions]);

  /**
   * Parse the passions response to extract user's selected passions
   * Handles the new API response format with direct passion keys
   */
  const getSelectedPassions = () => {
    if (!passions) return [];

    return extractSelectedPassionsMeta(passions).map((entry) => entry.id);
  };

  const getSelectedPassionsMeta = () => {
    if (!passions) {
      return [];
    }

    return extractSelectedPassionsMeta(passions);
  };

  /**
   * Check if a specific passion is selected by the user
   */
  const isPassionSelected = (passion) => {
    const selected = getSelectedPassions();
    return selected.includes(passion);
  };

  return {
    passions,
    loading,
    error,
    getSelectedPassions,
    getSelectedPassionsMeta,
    isPassionSelected,
    refetch: () => acid && fetchProfile(acid),
    profile: profileInfo,
  };
};